import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DataLayer } from '../../../models/data-layer';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { PaginatorModule } from 'primeng/paginator';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { yearValidator } from '../../../validators/year.validator';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { DataLayerType } from '../../../models/data-layer-type';
import { getState } from '@ngrx/signals';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { GlobalsStore } from '../../../stores/globals.state';
import { ButtonModule } from 'primeng/button';
import { SidebarModule } from 'primeng/sidebar';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { ImportExportGeodataComponent } from '../import-export-geodata/import-export-geodata.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-data-layer-form',
  standalone: true,
  imports: [
    PaginatorModule,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    InputTextModule,
    DividerModule,
    InputSwitchModule,
    InputTextareaModule,
    NgIf,
    MultiSelectModule,
    TypeDropdownComponent,
    FormFooterComponent,
    ButtonModule,
    SidebarModule,
    HeaderComponent,
    MaskComponent,
    ImportExportGeodataComponent,
    ConfirmDialogModule,
  ],
  templateUrl: './data-layer-form.component.html',
  styleUrl: './data-layer-form.component.scss',
})
export class DataLayerFormComponent
  extends BaseFormComponent<DataLayer>
  implements OnInit
{
  readonly store = inject(DataLayerStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly globalsStore = inject(GlobalsStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: DataLayer;
  override formInitialized = false;
  private _lastSelectedId?: string;
  @Input() parent?: DataLayerType;
  @Input() parentId?: string;
  showGeoImport = false;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const layerState = getState(this.store);
      const selectedId = layerState.selectedId;
      const itemToEditId = this.itemToEdit?._id;

      if (
        this.parent &&
        this.formInitialized &&
        selectedId &&
        selectedId !== itemToEditId &&
        selectedId !== this._lastSelectedId
      ) {
        this._lastSelectedId = selectedId;

        setTimeout(() => this.initializeForm(), 300);
      }
    });
  }

  override ngOnInit() {
    if (this.parentId) {
      this.parent = this.dataLayerTypeStore.getById(this.parentId);
    }
    super.ngOnInit();
  }

  /**
   * Initializes the form with default or pre-filled values based on the currently selected item and its parent.
   * Sets up form controls, validation, and other necessary configurations.
   *
   * @return {void} This method does not return a value.
   */
  initializeForm(): void {
    this.itemToEdit = this.forceNew ? undefined : this.store.selectedItem();

    if (!this.parent && this.itemToEdit && this.itemToEdit.parent) {
      this.parent = this.dataLayerTypeStore.getById(this.itemToEdit.parent);
    }

    this.form = this.fb.group({
      year: [
        {
          value: this.itemToEdit?.year || '',
          disabled: !!this.itemToEdit?.year,
        },
        yearValidator(
          this.parent ? this.store.getYearsByParent(this.parent?._id) : []
        ),
      ],
      description: [this.itemToEdit?.description || ''],
      published: [this.itemToEdit?.published || false],
      isForecast: [this.itemToEdit?.isForecast || false],
      parent: [{ value: this.parent, disabled: !!this.parent }],
    });

    this.formSubmitted.subscribe(value => {});
    this.formInitialized = true;
  }

  /**
   * Handles the change event of a parent dropdown or data layer type.
   *
   * @param {DropdownChangeEvent | DataLayerType} event The change event containing information about the selected parent or data layer.
   * @return {void} This method does not return a value.
   */
  onParentChange(event: DropdownChangeEvent | DataLayerType): void {
    const years = this.store.getYearsByParent(event.value._id);
    this.form.get('year')?.setValidators([yearValidator(years)]);
  }

  /**
   * Removes a specified data layer from the application.
   * Initiates a confirmation dialog to confirm the deletion process before removal.
   *
   * @param {DataLayer} item - The data layer to be removed.
   * @return {void} This method does not return any value.
   */
  removeDataLayer(item: DataLayer): void {
    this.confirmationDialogService.confirmDataLayerDeletion(
      undefined,
      item,
      () => this.remove(item)
    );
  }

  /**
   * Handles form submission to prepare and send data to the server. The method
   * manages the creation of a `dataLayer` object based on the form's values and additional
   * logic to determine the type and geoData structure based on the parent element.
   *
   * Modifies the `dataLayer` type to "Point" if the parent has an icon, otherwise defaults to "Polygon".
   * It also links geoData from sibling records, if available, and associates the parent ID for hierarchical structuring.
   * Finally, it invokes the `submit` method to send the `dataLayer` object.
   *
   * @return {void} No value is returned from this method.
   */
  onSubmit(): void {
    const dataLayer: DataLayer = { ...this.form.value };
    const parent = this.parent || this.form.value.parent;
    if (parent.icon) {
      dataLayer.type = 'Point';
      delete dataLayer.geoData;
    } else {
      dataLayer.type = 'Polygon';
    }
    dataLayer.geoData =
      this.store.getGeoDataIdFromSiblings(parent._id) || undefined;
    dataLayer.parent = parent._id;

    this.submit(dataLayer);
  }

  /**
   * Whether the "Gebietsstandsänderung" geo-data import button should be
   * shown: only for an existing year of a Polygon-type DataLayerType, and
   * only with geo-import rights.
   *
   * @return {boolean} True if the button should be shown.
   */
  canImportGeoDataForYear(): boolean {
    return (
      !!this.itemToEdit &&
      this.parent?.type === 'Polygon' &&
      this.permissionService.canImportGeoData()
    );
  }

  /**
   * Asks for confirmation, then seeds the global Type/Layer selection with
   * this year and opens the scoped geo-data import sidebar so the user can
   * react to a Gebietsstandsänderung for just this one year.
   *
   * @return {void} Does not return a value.
   */
  startGeoDataImport(): void {
    if (!this.itemToEdit || !this.parent) return;

    this.confirmationDialogService.confirmGeoDataDecouple(
      this.itemToEdit.year,
      () => {
        if (!this.itemToEdit || !this.parent) return;
        this.globalsStore.setSelectedType(this.parent, true);
        this.globalsStore.setSelectedLayer(this.itemToEdit);
        this.showGeoImport = true;
      }
    );
  }

  /**
   * Closes the scoped geo-data import sidebar.
   *
   * @return {void} Does not return a value.
   */
  closeGeoDataImport(): void {
    this.showGeoImport = false;
  }
}
